const DARK_TEXT = '#1a1a1a';
const LIGHT_TEXT = '#ffffff';

/** Luminância relativa (WCAG 2.x) de uma cor `#rrggbb` ou `#rgb`. */
const luminance = (hex: string): number => {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((char) => char + char)
          .join('')
      : clean;

  const [r, g, b] = [0, 2, 4].map((start) => {
    const channel = parseInt(full.slice(start, start + 2), 16) / 255;

    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a: number, b: number): number =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/**
 * A cor de texto legível sobre um fundo: branco ou quase preto, o que der mais
 * contraste.
 *
 * ⚠️ **Existe porque as cores de fundo são da spec e o texto branco não é.** As
 * etiquetas de gravidade e categoria (`02-alertas.md`) são tons médios — salmão,
 * laranja, verde claro —, e branco sobre eles fica entre 1,9:1 e 2,9:1, abaixo dos
 * 4,5:1 do WCAG AA (achado 4 do QA de 18/09: a etiqueta "Grave" parecia um borrão).
 * Escolher o texto pelo fundo mantém a cor da spec e dispensa uma tabela paralela de
 * "cor do texto" que alguém esqueceria de atualizar.
 */
export default function readableTextOn(background: string): string {
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(background)) {
    return LIGHT_TEXT;
  }

  const bg = luminance(background);

  return contrast(bg, luminance(LIGHT_TEXT)) >=
    contrast(bg, luminance(DARK_TEXT))
    ? LIGHT_TEXT
    : DARK_TEXT;
}
